/**
 *
 * DndControl
 *
 * "Do not disturb" for the psychologists portal: new-call / new-chat stop ringing and showing toasts for 1 h,
 * 2 h or until the end of the day (Europe/Lisbon); pending SOS calls and chats still reach the Home lists.
 * variant="header": desktop pill (same shape as the language picker) opening a dropdown of durations.
 * variant="sidebar": mobile drawer item with the same options.
 */

import "./styles.scss";

import { Dropdown, Menu, Tooltip, notification } from "antd";

import { DateTime } from "luxon";
import { Icons } from "components";
import React from "react";
import Strings from "utils/strings";
import { connect } from "react-redux";
import { isPsychologistsPortal } from "utils/portal";
import { setDnd } from "store/actions";

const LISBON_ZONE = "Europe/Lisbon";

type DndOption = "1h" | "2h" | "day";

const formatUntil = (until: number) => DateTime.fromMillis(until).setZone(LISBON_ZONE).toFormat("HH:mm");

export class DndControl extends React.Component<any, any> {
	isActive() {
		const { dnd } = this.props;

		return Boolean(dnd?.until) && dnd.until > Date.now();
	}

	statusLabel(active: boolean) {
		const { dnd } = this.props;

		return active ? Strings.formatString(Strings.dnd.activeUntil, formatUntil(dnd.until)) : Strings.dnd.title;
	}

	activate(option: DndOption) {
		const { dispatch } = this.props;
		const now = DateTime.local();
		let until: DateTime;

		if (option === "1h") until = now.plus({ hours: 1 });
		else if (option === "2h") until = now.plus({ hours: 2 });
		else until = now.setZone(LISBON_ZONE).endOf("day");

		dispatch(setDnd({ until: until.toMillis() }));

		notification.success({
			message: Strings.dnd.title,
			description: Strings.formatString(Strings.dnd.activatedFeedback, formatUntil(until.toMillis())),
			placement: "bottomRight",
			duration: 4
		});
	}

	deactivate() {
		const { dispatch } = this.props;

		dispatch(setDnd({ until: null }));

		notification.success({
			message: Strings.dnd.title,
			description: Strings.dnd.deactivatedFeedback,
			placement: "bottomRight",
			duration: 4
		});
	}

	renderIcon(active: boolean) {
		return active ? <Icons.BellOff /> : <Icons.Bell />;
	}

	renderHeader() {
		const active = this.isActive();
		const itemStyle = { height: 40, display: "flex", alignItems: "center" };
		const separatorStyle = { height: 1, backgroundColor: "#EEE", padding: 0 };

		const items: any[] = [
			{ label: Strings.dnd.chooseDuration, key: "hint", disabled: true, style: itemStyle },
			{ key: "separator0", style: separatorStyle },
			{ label: Strings.dnd.oneHour, key: "1h", style: itemStyle, onClick: () => this.activate("1h") },
			{ key: "separator1", style: separatorStyle },
			{ label: Strings.dnd.twoHours, key: "2h", style: itemStyle, onClick: () => this.activate("2h") },
			{ key: "separator2", style: separatorStyle },
			{ label: Strings.dnd.untilEndOfDay, key: "day", style: itemStyle, onClick: () => this.activate("day") }
		];

		if (active) {
			items.push({ key: "separator3", style: separatorStyle });
			items.push({ label: Strings.dnd.turnOff, key: "off", style: itemStyle, onClick: () => this.deactivate() });
		}

		return (
			<Dropdown overlay={<Menu items={items} />} trigger={["click"]}>
				<button type="button" className={`DndControl${active ? " --active" : ""}`}>
					{/* Tooltip on the icon + label only, so it does not stack another trigger on the Dropdown child */}
					<Tooltip title={active ? Strings.dnd.tooltip : Strings.dnd.tooltipInactive}>
						<span className="DndControlContent">
							{this.renderIcon(active)}
							<span className="DndControlLabel">{this.statusLabel(active)}</span>
						</span>
					</Tooltip>
					<Icons.Caret />
				</button>
			</Dropdown>
		);
	}

	renderSidebar() {
		const active = this.isActive();

		return (
			<div className={`DndControl --sidebar${active ? " --active" : ""}`}>
				<div className="DndControlSidebarTitle">
					<span className="SidebarMenuIcon">{this.renderIcon(active)}</span>
					<p>{this.statusLabel(active)}</p>
				</div>
				<div className="DndControlSidebarOptions">
					{active ? (
						<button onClick={() => this.deactivate()}>{Strings.dnd.turnOff}</button>
					) : (
						<React.Fragment>
							<button onClick={() => this.activate("1h")}>{Strings.dnd.oneHour}</button>
							<div className="ButtonSeparator" />
							<button onClick={() => this.activate("2h")}>{Strings.dnd.twoHours}</button>
							<div className="ButtonSeparator" />
							<button onClick={() => this.activate("day")}>{Strings.dnd.untilEndOfDay}</button>
						</React.Fragment>
					)}
				</div>
				{active && <p className="DndControlSidebarHint">{Strings.dnd.tooltip}</p>}
			</div>
		);
	}

	render() {
		const { variant } = this.props;

		if (!isPsychologistsPortal()) return null;

		return variant === "sidebar" ? this.renderSidebar() : this.renderHeader();
	}
}

// `language` is mapped only to re-render on PT/EN/ES changes (same pattern as components/Breadcrumb).
const mapStateToProps = (state: any) => ({
	dnd: state.dnd,
	language: state.language
});

export default connect(mapStateToProps)(DndControl);
